import { append, assocPath, filter, map, mergeRight, insert, remove, isEmpty, update, mergeAll, clone } from 'ramda';

import constants from '../constants';
import createInitialState from '../lib/createInitialState';
import { appendIfNot, removeIfExists } from '../lib/constraints';
import { FieldJson, FormJson } from 'edocatbase/Form';
import { Action } from 'edocatbase/Global';

import { formDisabled } from '../lib/validation';
import getFieldEmptyValue from '../lib/value';

const formStateSwitch = (state: FormJson, action: Action) => {
	switch (action.type) {
		case constants.INIT_FORM: {
			const { payload, formInstanceId } = action;
			return createInitialState(payload, formInstanceId);
		}

		case constants.DESTROY_FORM: {
			return {};
		}

		case constants.ON_FORM_CHANGE: {
			const { path, value, addValueField } = action.payload;
			return assocPath(['fields', ...path, ...(!path.includes('value') && addValueField ? ['value'] : [])], value, state);
		}

		case constants.ON_FORM_SUBMIT_PROMISE_SUCCESS: {
			if (!state.stayOnPage) {
				window.location.assign(state.urlAfterSubmit);
				return {
					...state,
					submitted: true,
				};
			}
			const fields = map(
				field => ({
					...field,
					focus: false,
					edit: false,
				}),
				state.fields
			);

			return {
				...state,
				disabled: false,
				submitted: state.allowChangeAfterSubmit ? false : state.successCallback || !state.stayOnPage,
				fields,
				edit: false,
			};
		}
		// For experimental Ares component - DELETE AFTER MM REFACTOR
		case constants.ADD_ARES_FIELD: {
			const name = action.payload;
			const defaultValue = {
				companyName: '',
				ico: '',
				dic: '',
				town: '',
				street: '',
				zip: '',
				aresError: false,
				contacts: [],
			};
			const newFields = append(defaultValue, state.fields?.[name]?.value);
			return assocPath(['fields', name, 'value'], newFields, state);
		}

		case constants.REMOVE_ARES_FIELD: {
			const { name, index } = action.payload;
			const newFields = remove(index, 1, state.fields?.[name]?.value);
			return assocPath(['fields', name, 'value'], newFields, state);
		}

		case constants.ADD_NEW_CONTACT: {
			const { index, value, name } = action.payload;
			const newContacts = append(value, state.fields?.[name]?.value[index]?.contacts);
			return assocPath(['fields', name, 'value', index, 'contacts'], newContacts, state);
		}

		case constants.FETCH_ARES_VALUES_ERROR: {
			const { index, name } = action.payload;
			const newFields = update(
				index,
				{
					...state.fields?.[name]?.value?.[index],
					aresError: true,
				},
				state.fields?.[name]?.value
			);
			return assocPath(['fields', name, 'value'], newFields, state);
		}

		case constants.FETCH_ARES_VALUES_SUCCESS: {
			const { index, name, response } = action.payload;
			// Update fields according to ARES
			const newFields = update(
				index,
				{
					...state.fields?.[name]?.value?.[index],
					...response,
					aresError: false,
				},
				state.fields?.[name]?.value
			);

			return assocPath(['fields', name, 'value'], newFields, state);
		}

		case constants.MAKE_FIELD_DIRTY: {
			const name = action.payload;
			return assocPath(['fields', name, 'dirty'], true, state);
		}

		case constants.CLEAN_FIELD_DIRTY: {
			const name = action.payload;
			return assocPath(['fields', name, 'dirty'], false, state);
		}

		case constants.MAKE_ALL_FIELDS_DIRTY: {
			if (!state.fields) {
				return { ...state };
			}
			const fieldsObj = { ...state.fields };
			Object.keys(fieldsObj).map((key: string) => { fieldsObj[key].dirty = true; });
			return {
				...state,
				fields: fieldsObj
			};
		}

		case constants.HIGHLIGHT_ERRORS: {
			const fields = state.fields
				? map(field => mergeRight(field, { error: action.payload[field.name] }), state.fields)
				: state.fields;

			const errors = Object.keys(action.payload);

			return {
				...state,
				// @ts-ignore
				hasErrors: formDisabled(fields),
				error: errors,
				fields,
			};
		}

		case constants.HIGHLIGHT_CURRENT_FIELD: {
			const { name, errorType } = action.payload;

			return {
				...state,
				fields: assocPath([name, 'error'], errorType, state.fields),
			};
		}

		case constants.CLEAR_FORM_VALUES: {
			return {
				...state,
				// @ts-ignore
				fields: map(
					(field: FieldJson) => ({
						...field,
						value: getFieldEmptyValue(field),
					}),
					state.fields
				),
			};
		}

		case constants.REMOVE_ASSOC_ITEM: {
			const { name, item } = action.payload;
			const index = state.fields[name].value.findIndex((assocItem: any) => assocItem.nodeRef === item);
			const newValue = remove(index, 1, state.fields[name].value);

			return assocPath(['fields', name, 'value'], newValue, state);
		}

		case constants.REARRANGE_ASSOC_ITEMS: {
			const { name, item, index, direction } = action.payload;
			let newValue = [];
			if (direction === 'up') {
				newValue = remove(index, 1, state.fields[name].value);
				newValue = insert(index - 1, item, newValue);
			} else {
				newValue = remove(index, 1, state.fields[name].value);
				newValue = insert(index + 1, item, newValue);
			}

			return assocPath(['fields', name, 'value'], newValue, state);
		}

		case constants.CLEAR_AUTHORITY_OPTIONS: {
			const name = action.payload;
			return assocPath(['fields', name, 'options'], [], state);
		}

		case constants.ADD_CONSTRAINT: {
			const fieldId = action.payload.fieldId;
			const fieldExists = fieldId ? state.fields[fieldId] : false;
			if (!fieldExists) {
				return state;
			}

			const isMandatory = action.payload.id === 'MANDATORY';
			const fields = fieldId && isMandatory ? assocPath([fieldId, 'mandatory'], true, state.fields) : state.fields;

			return {
				...state,
				constraints: appendIfNot(action.payload, state.constraints),
				fields,
			};
		}

		case constants.REMOVE_CONSTRAINT: {
			const fieldId = action.payload.fieldId;
			const fieldExists = fieldId ? state.fields[fieldId] : false;
			if (!fieldExists) {
				return state;
			}

			const isMandatory = action.payload.id === 'MANDATORY' || action.payload.id === 'ISO_MANDATORY';
			const fields = fieldId && isMandatory ? assocPath([fieldId, 'mandatory'], false, state.fields) : state.fields;

			return {
				...state,
				constraints: removeIfExists(action.payload, state.constraints),
				fields,
			};
		}

		case constants.DROP_VALIDATION: {
			return {
				...state,
				constraints: [],
			};
		}

		case constants.GET_SELECT_VALUE: {
			const { name, multi } = action.payload;
			const field = state?.fields?.[name];
			const options = field?.options ?? [];

			let value = null;
			if (multi) {
				// @ts-ignore
				const filteredOptions = (options).filter((option: any) =>
					field.value.some((it: any) => it.value === option.value || it === option.value)
				);
				value =
					filteredOptions.length > 0
						? filteredOptions
						: Array.isArray(field?.lastValue)
							? field?.lastValue
							: [field?.lastValue];
			} else {
				// @ts-ignore
				value = (options).find((option: any) =>
					option.value === field?.value
					|| option.value === field?.value?.value)
					|| field?.lastValue;
			}
			return value ? assocPath(['fields', name, 'value'], value, state) : state;

		}

		case constants.ON_FORM_SUBMIT_PROMISE_START: {
			return {
				...state,
				disabled: true,
			};
		}

		case constants.ON_FORM_SUBMIT_PROMISE_ERROR: {
			return {
				...state,
				disabled: false,
			};
		}

		case constants.FETCH_FIELD_OPTIONS_SUCCESS: {
			const { name, response, newValue } = action.payload;
			const options = response.list.entries.map(item => ({
				label: item.entry.tag,
				value: `workspace://SpacesStore/${item.entry.id}`,
			}));

			if (newValue) {
				options.push(newValue);
			}

			const values = newValue
				? [...(state.fields?.[name]?.value || []).map(item => item.value), newValue.value]
				: state.fields?.[name]?.value || [];

			const value = options.reduce((acc, next) => (values.includes(next.value) ? [...acc, next] : acc), []);

			const field = {
				...state.fields?.[name],
				options,
				value,
			};

			return assocPath(['fields', name], field, state);
		}

		case constants.CREATE_NEW_CATEGORY_ITEM_PROMISE_SUCCESS: {
			const {
				name,
				response: { name: label, nodeRef },
			} = action.payload;

			return {
				...state,
				fields: {
					...state.fields,
					[name]: {
						...state.fields[name],
						value: append(
							{
								label,
								value: nodeRef,
							},
							state.fields[name].value
						),
						options: append({ label, value: nodeRef }, state.fields[name].options),
					},
				},
			};
		}

		case constants.FETCH_ITEMS_BY_NODES_FOR_TABLE_SUCCESS: {
			const {
				name,
				response: {
					data: { items },
				},
				key,
				index,
			} = action.payload;
			const multi = state.fields[name].repeating;
			const value = map(
				item => ({
					...item,
					title: item?.title ?? '',
				}),
				items
			);
			return assocPath(['fields', name, 'value', index, key], multi ? value : value[0], state);
		}

		case constants.FETCH_ITEMS_BY_NODES_SUCCESS: {
			const {
				name,
				response: {
					data: { items },
				},
			} = action.payload;

			const multi = state?.fields?.[name]?.repeating;
			let nameField =
				state?.fields?.[name]?.control?.params && state?.fields?.[name]?.control?.params.labelField
					? state.fields[name].control.params.labelField
					: null;

			if (state?.fields?.[name]?.control?.template.endsWith('asyncselect.ftl')) {
				nameField = 'title';
			}

			const value = map(
				item => ({
					label: item[nameField ? nameField : item?.title ? 'title' : 'name'],
					value: item.nodeRef,
					...item,
				}),
				items
			);
			
			//TODO toto je hnusohack, proc redux dela na na jeden call akci 3x pokazde s jinym statem?	
			return typeof multi === 'undefined'? state: assocPath(['fields', name, 'value'], multi ? value : value[0], state);
		}

		case constants.FETCH_VALUE_BY_NODES_SUCCESS: {
			const {
				name,
				response: {
					data: { items },
				},
			} = action.payload;
			
			if(state.fields?.[name]){
				return assocPath(['fields', name, 'value'], items, state);
			} else {
				return state;
			}
		}

		case constants.FETCH_INIT_DATA_BOX_SUCCESS: {
			const {
				response,
				params: { label, value, collateralChangeFields, collateralChangeEqualFields },
				name,
			} = action.payload;

			const item = response[0] || {};

			const option = {
				label: item[label],
				value: item[value],
			};

			const newFields = {
				...state.fields,
				[name]: {
					...state.fields[name],
					options: !isEmpty(item) ? [option] : [],
					value: !isEmpty(item) ? option : null,
				},
			};

			if (collateralChangeFields) {
				const ids = collateralChangeFields.split(',');
				const keys = collateralChangeEqualFields.split('|#alf#|');

				ids.forEach((id, index) => {
					if (newFields[id]) {
						newFields[id].value = item[keys[index]];
					}
				});
			}

			return assocPath(['fields'], newFields, state);
		}

		case constants.FETCH_DATA_BOX_SUCCESS: {
			const {
				response,
				params: { label, value },
				name,
			} = action.payload;

			return assocPath(
				['fields', name],
				{
					...state.fields[name],
					options: filter(
						(row: any) => row.label,
						map(
							item => ({
								...item,
								label: item[label],
								value: item[value],
							}),
							response
						)
					),
				},
				state
			);
		}

		case constants.FETCH_UNITS_SUCCESS: {
			return assocPath(
				['fields', action.payload.name],
				{
					...state.fields[action.payload.name],
					options: map(
						item => ({
							title: item.title,
							name: item.name,
							persons: map(
								person => ({
									name: `${person.firstName || ''} ${person.lastName || ''}`,
									userName: person.userName,
								}),
								item.persons
							),
						}),
						action.payload.response
					),
				},
				state
			);
		}

		case constants.TRANSITION_SUBMIT: {
			return {
				...state,
				transitionSubmit: action.payload,
			};
		}

		case constants.PROMISE_UPLOAD_PACKAGE_ITEMS_SUCCESS: {
			const { name, nodeRef, cm_name } = action.payload;
			return assocPath(['fields', name, 'value'], [...state.fields[name].value, { name: cm_name, nodeRef }], state);
		}

		case constants.PROMISE_UPLOAD_ATTACHMENT_SUCCESS: {
			const { name, nodeRef, fileName } = action.payload;
			return assocPath(['fields', name, 'value'], [...state.fields[name].value, { name: fileName, nodeRef }], state);
		}

		default: {
			return state;
		}
	}
};

export default formStateSwitch;
